import { apiClient } from "./client";
import type { Post, PostImage } from "@abejon/shared";

interface PostWithImages extends Post {
  images: PostImage[];
}

interface PostDetailResponse {
  post: Post;
  images: PostImage[];
  business: {
    displayName: string;
    slug: string;
    avatarUrl: string | null;
  } | null;
  isLiked: boolean;
}

export interface CommentWithUser {
  id: string;
  postId: string;
  userId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  userName: string;
  userAvatarUrl: string | null;
}

export async function createPost(data: {
  caption?: string;
  images: {
    r2Key: string;
    imageUrl: string;
    width?: number;
    height?: number;
    sortOrder: number;
  }[];
}) {
  return apiClient<{ post: Post; images: PostImage[] }>("/posts", {
    method: "POST",
    body: data,
  });
}

export async function getPost(id: string) {
  return apiClient<PostDetailResponse>(`/posts/${id}`);
}

export async function updatePost(id: string, caption: string | null) {
  return apiClient<{ post: Post }>(`/posts/${id}`, {
    method: "PATCH",
    body: { caption },
  });
}

export async function deletePost(id: string) {
  return apiClient<{ message: string }>(`/posts/${id}`, {
    method: "DELETE",
  });
}

export async function getBusinessPosts(slug: string, page = 1) {
  return apiClient<{ posts: PostWithImages[]; page: number; limit: number }>(
    `/businesses/${slug}/posts?page=${page}`
  );
}

// ─── Likes ───────────────────────────────────────────────

export async function likePost(id: string) {
  return apiClient<{ liked: boolean }>(`/posts/${id}/like`, {
    method: "POST",
  });
}

export async function unlikePost(id: string) {
  return apiClient<{ liked: boolean }>(`/posts/${id}/like`, {
    method: "DELETE",
  });
}

// ─── Comments ────────────────────────────────────────────

export async function getComments(postId: string, page = 1) {
  return apiClient<{ comments: CommentWithUser[]; page: number; limit: number }>(
    `/posts/${postId}/comments?page=${page}`
  );
}

export async function createComment(postId: string, body: string) {
  return apiClient<{ comment: CommentWithUser }>(`/posts/${postId}/comments`, {
    method: "POST",
    body: { body },
  });
}

export async function deleteComment(postId: string, commentId: string) {
  return apiClient<{ message: string }>(`/posts/${postId}/comments/${commentId}`, {
    method: "DELETE",
  });
}
